export const JOURNEY = [
  { step: '01', title: 'Discover', text: 'Understand your interests, strengths, personality, and career possibilities.' },
  { step: '02', title: 'Decide', text: 'Choose the right stream, course, college, and career direction.' },
  { step: '03', title: 'Develop', text: 'Build the skills, certifications, projects, and experience required for your target career.' },
  { step: '04', title: 'Grow', text: 'Prepare for internships, placements, job opportunities, career transitions, and long-term growth.' },
];

export const ROADMAP_STEPS = ['School', 'Stream', 'Degree', 'Skills', 'Certification', 'Projects', 'Internship', 'Placement', 'Career Growth'];

export const SERVICES = [
  { icon: 'Compass', title: 'Career Guidance', text: 'Personalised guidance to identify suitable career directions.' },
  { icon: 'GitBranch', title: 'Stream Selection', text: 'Guidance for choosing the right academic stream after 10th.' },
  { icon: 'BookOpen', title: 'Course Selection', text: 'Understand courses, specialisations, career opportunities, and future scope.' },
  { icon: 'Building2', title: 'College Selection', text: 'Make informed college decisions based on goals, budget, location, and career outcomes.' },
  { icon: 'Map', title: 'Career Roadmapping', text: 'Create a step-by-step roadmap from where you are today to your target career.' },
  { icon: 'Sparkles', title: 'Skill Development', text: 'Identify the skills required for your desired career and create a practical learning plan.' },
  { icon: 'TrendingUp', title: 'Upskilling', text: 'Find relevant certifications, tools, technologies, and industry skills.' },
  { icon: 'Briefcase', title: 'Placement & Job Readiness', text: 'Resume building, interview preparation, communication, professional skills, and job-readiness support.' },
];

export const AUDIENCES = [
  {
    icon: 'GraduationCap',
    title: 'School Students',
    tag: '8th–10th Grade',
    text: 'Discover interests and understand future career possibilities before making important academic decisions.',
    cta: 'Explore School Guidance',
    to: '/career-guidance',
  },
  {
    icon: 'GitBranch',
    title: 'Stream Selection',
    tag: '11th–12th Grade',
    text: 'Get clarity on choosing the right stream based on interests, strengths, and future career goals.',
    cta: 'Choose Your Stream',
    to: '/career-guidance',
  },
  {
    icon: 'School',
    title: 'College & Course Selection',
    tag: 'UG Aspirants',
    text: 'Compare courses, colleges, career opportunities, skills required, and future pathways.',
    cta: 'Explore Courses',
    to: '/career-guidance',
  },
  {
    icon: 'Rocket',
    title: 'Graduates & Professionals',
    tag: 'Career Growth',
    text: 'Identify skill gaps, explore career opportunities, and build a roadmap for your next career move.',
    cta: 'Plan Your Career',
    to: '/career-guidance',
  },
];

export const CAREER_CATEGORIES = [
  {
    id: 'ai-data',
    name: 'AI & Data Science',
    careers: [
      {
        name: 'Data Scientist',
        suitableFor: 'Students strong in maths and statistics who enjoy finding patterns in data.',
        skills: ['Python', 'Statistics', 'Machine Learning', 'Data Visualisation'],
        courses: ['B.Sc / B.Tech (CS, Stats, Maths)', 'M.Sc Data Science', 'ML & Deep Learning certifications'],
        progression: ['Data Analyst', 'Data Scientist', 'Senior Data Scientist', 'Head of Data'],
      },
      {
        name: 'Machine Learning Engineer',
        suitableFor: 'Students who love both coding and mathematics.',
        skills: ['Python', 'Deep Learning', 'MLOps', 'Linear Algebra'],
        courses: ['B.Tech CS / AI', 'M.Tech AI-ML', 'Cloud ML certifications'],
        progression: ['ML Intern', 'ML Engineer', 'Senior ML Engineer', 'AI Architect'],
      },
      {
        name: 'Data Analyst',
        suitableFor: 'Students who enjoy numbers, business questions, and storytelling with data.',
        skills: ['SQL', 'Excel', 'Power BI / Tableau', 'Python basics'],
        courses: ['Any UG degree', 'B.Sc Statistics / CS', 'Analytics certifications'],
        progression: ['Junior Analyst', 'Data Analyst', 'Senior Analyst', 'Analytics Manager'],
      },
    ],
  },
  {
    id: 'software-it',
    name: 'Software & IT',
    careers: [
      {
        name: 'Full-Stack Developer',
        suitableFor: 'Students who enjoy building real products with code.',
        skills: ['JavaScript', 'React', 'Node.js', 'Databases'],
        courses: ['B.Tech / BCA / B.Sc CS', 'Full-stack programs', 'Open-source projects'],
        progression: ['Intern', 'Developer', 'Senior Developer', 'Tech Lead'],
      },
      {
        name: 'Mobile App Developer',
        suitableFor: 'Students excited about apps and product building.',
        skills: ['Flutter / React Native', 'Kotlin / Swift', 'APIs', 'UI fundamentals'],
        courses: ['B.Tech / BCA', 'App development specialisations', 'Published apps portfolio'],
        progression: ['App Developer', 'Senior Developer', 'Product Engineer', 'CTO track'],
      },
      {
        name: 'DevOps Engineer',
        suitableFor: 'Students who like systems, automation, and reliability.',
        skills: ['Linux', 'Cloud (AWS / Azure)', 'CI/CD', 'Docker & Kubernetes'],
        courses: ['B.Tech CS / IT', 'Cloud certifications', 'DevOps projects'],
        progression: ['Cloud Engineer', 'DevOps Engineer', 'Senior DevOps', 'Platform Lead'],
      },
    ],
  },
  {
    id: 'finance',
    name: 'Finance & Accounting',
    careers: [
      {
        name: 'Chartered Accountant',
        suitableFor: 'Commerce students with strong accounting aptitude and discipline.',
        skills: ['Accounting', 'Taxation', 'Audit', 'Financial Reporting'],
        courses: ['B.Com + CA Foundation to Final', 'Articleship', 'ICAI examinations'],
        progression: ['Articleship Trainee', 'Qualified CA', 'Senior Manager', 'Partner / CFO'],
      },
      {
        name: 'Financial Analyst',
        suitableFor: 'Students who enjoy finance, markets, and working with numbers.',
        skills: ['Financial Modelling', 'Excel', 'Valuation', 'Accounting basics'],
        courses: ['B.Com / BBA Finance', 'CFA / FRM (optional)', 'MBA Finance'],
        progression: ['Analyst', 'Senior Analyst', 'Finance Manager', 'VP Finance'],
      },
      {
        name: 'Investment Banker',
        suitableFor: 'Ambitious graduates comfortable with high-intensity finance work.',
        skills: ['Valuation', 'M&A fundamentals', 'Excel & PowerPoint', 'Markets knowledge'],
        courses: ['B.Com / BBA / Economics', 'MBA (premier institutes)', 'CFA'],
        progression: ['Analyst', 'Associate', 'Vice President', 'Director / MD'],
      },
    ],
  },
  {
    id: 'business',
    name: 'Business & Management',
    careers: [
      {
        name: 'Product Manager',
        suitableFor: 'Students who enjoy the intersection of business, technology, and user problems.',
        skills: ['User Research', 'Analytics', 'Communication', 'Prioritisation'],
        courses: ['Any UG + internships', 'MBA (optional)', 'Product certifications'],
        progression: ['Associate PM', 'Product Manager', 'Senior PM', 'Head of Product'],
      },
      {
        name: 'Business Analyst',
        suitableFor: 'Students who like solving business problems with data.',
        skills: ['Excel & SQL', 'Requirement Analysis', 'Documentation', 'Communication'],
        courses: ['BBA / B.Com / B.Tech', 'ECBA / CBAP certifications', 'Internships'],
        progression: ['Junior BA', 'Business Analyst', 'Senior BA', 'Product / Strategy roles'],
      },
      {
        name: 'Management Consultant',
        suitableFor: 'High-achieving students who enjoy structured problem-solving.',
        skills: ['Problem Structuring', 'Presentation', 'Research', 'Analytics'],
        courses: ['Strong UG program', 'MBA from premier institutes', 'Case interview practice'],
        progression: ['Analyst', 'Consultant', 'Engagement Manager', 'Partner'],
      },
    ],
  },
  {
    id: 'marketing',
    name: 'Digital Marketing',
    careers: [
      {
        name: 'Performance Marketer',
        suitableFor: 'Students who like measurable, fast-paced marketing work.',
        skills: ['Google & Meta Ads', 'Analytics', 'Copywriting', 'A/B Testing'],
        courses: ['Any UG degree', 'Digital marketing certifications', 'Live campaign projects'],
        progression: ['Executive', 'Specialist', 'Performance Lead', 'Head of Growth'],
      },
      {
        name: 'SEO Specialist',
        suitableFor: 'Students who enjoy research, content, and long-term compounding results.',
        skills: ['Keyword Research', 'On-page & Technical SEO', 'Content Strategy', 'SEO tools'],
        courses: ['Any UG degree', 'SEO certifications', 'Portfolio of ranked sites'],
        progression: ['SEO Executive', 'SEO Specialist', 'SEO Manager', 'Head of Organic'],
      },
      {
        name: 'Content Strategist',
        suitableFor: 'Students who love writing, ideas, and audience psychology.',
        skills: ['Writing', 'Storytelling', 'Research', 'Content Analytics'],
        courses: ['Any UG (English / Journalism helps)', 'Content portfolio', 'Marketing certifications'],
        progression: ['Content Writer', 'Strategist', 'Content Lead', 'Brand & Content Head'],
      },
    ],
  },
  {
    id: 'cybersecurity',
    name: 'Cybersecurity',
    careers: [
      {
        name: 'Security Analyst',
        suitableFor: 'Students curious about systems, networks, and threats.',
        skills: ['Networking', 'Linux', 'SIEM tools', 'Security fundamentals'],
        courses: ['B.Tech CS / IT / Cyber', 'CompTIA Security+', 'SOC internships'],
        progression: ['SOC Analyst L1', 'Security Analyst', 'Senior Analyst', 'Security Manager'],
      },
      {
        name: 'Ethical Hacker',
        suitableFor: 'Students who enjoy breaking systems to make them stronger.',
        skills: ['Penetration Testing', 'Scripting', 'Web Security', 'CTF practice'],
        courses: ['B.Tech / B.Sc CS', 'CEH / OSCP certifications', 'Bug bounty practice'],
        progression: ['Pentest Intern', 'Penetration Tester', 'Senior Consultant', 'Red Team Lead'],
      },
      {
        name: 'SOC Analyst',
        suitableFor: 'Students who like investigation, monitoring, and incident response.',
        skills: ['Log Analysis', 'Incident Response', 'Networking', 'Threat Intelligence'],
        courses: ['B.Tech / BCA', 'Security+ / CySA+', 'Hands-on SOC labs'],
        progression: ['L1 Analyst', 'L2 / L3 Analyst', 'Incident Responder', 'SOC Lead'],
      },
    ],
  },
  {
    id: 'engineering',
    name: 'Core Engineering',
    careers: [
      {
        name: 'Mechanical Design Engineer',
        suitableFor: 'Students who enjoy machines, design, and manufacturing.',
        skills: ['CAD (SolidWorks / AutoCAD)', 'GD&T', 'Manufacturing basics', 'FEA'],
        courses: ['B.E. / B.Tech Mechanical', 'CAD / CAE certifications', 'Core internships'],
        progression: ['Graduate Trainee', 'Design Engineer', 'Senior Engineer', 'Engineering Manager'],
      },
      {
        name: 'Civil Site Engineer',
        suitableFor: 'Students who like on-ground execution and infrastructure.',
        skills: ['AutoCAD', 'Estimation', 'Site Management', 'Quality Control'],
        courses: ['B.E. Civil', 'Site internships', 'Project management certifications'],
        progression: ['Site Engineer', 'Senior Engineer', 'Project Manager', 'General Manager'],
      },
      {
        name: 'Electrical Engineer',
        suitableFor: 'Students interested in power, circuits, and industrial systems.',
        skills: ['Circuit Design', 'PLC basics', 'Power Systems', 'MATLAB'],
        courses: ['B.E. EEE / ECE', 'Industry certifications', 'Core internships'],
        progression: ['Graduate Engineer', 'Electrical Engineer', 'Senior Engineer', 'Plant / Project Lead'],
      },
    ],
  },
  {
    id: 'design',
    name: 'Design & Creative Careers',
    careers: [
      {
        name: 'UX/UI Designer',
        suitableFor: 'Students who mix creativity with problem-solving.',
        skills: ['Figma', 'User Research', 'Wireframing', 'Visual Design'],
        courses: ['Any UG + design portfolio', 'Design bootcamps', 'Case-study projects'],
        progression: ['Junior Designer', 'UX/UI Designer', 'Senior Designer', 'Design Lead'],
      },
      {
        name: 'Graphic Designer',
        suitableFor: 'Students with strong visual sense and an eye for detail.',
        skills: ['Adobe Suite', 'Typography', 'Branding', 'Layout Design'],
        courses: ['B.Des / BFA or any UG + portfolio', 'Design certifications', 'Freelance projects'],
        progression: ['Junior Designer', 'Graphic Designer', 'Art Director', 'Creative Director'],
      },
      {
        name: 'Motion Designer',
        suitableFor: 'Students who love animation, video, and storytelling.',
        skills: ['After Effects', 'Storyboarding', '2D / 3D Animation', 'Editing'],
        courses: ['Any UG + showreel', 'Motion design courses', 'Freelance work'],
        progression: ['Motion Intern', 'Motion Designer', 'Senior Motion Designer', 'Creative Lead'],
      },
    ],
  },
  {
    id: 'healthcare',
    name: 'Healthcare',
    careers: [
      {
        name: 'Doctor (MBBS)',
        suitableFor: 'Biology students committed to long-term medical study.',
        skills: ['Biology', 'Discipline', 'Empathy', 'Decision-making'],
        courses: ['NEET-UG → MBBS', 'MD / MS specialisation', 'Clinical rotations'],
        progression: ['MBBS Student', 'Intern / Junior Doctor', 'Resident / MD', 'Specialist / Consultant'],
      },
      {
        name: 'Pharmacist',
        suitableFor: 'Students interested in medicine, chemistry, and healthcare systems.',
        skills: ['Pharmaceutical Science', 'Attention to Detail', 'Regulations', 'Communication'],
        courses: ['D.Pharm / B.Pharm', 'M.Pharm (optional)', 'Licensing exams'],
        progression: ['Pharmacist', 'Senior Pharmacist', 'QA / Regulatory roles', 'Pharma Management'],
      },
      {
        name: 'Physiotherapist',
        suitableFor: 'Students who want hands-on patient care and rehabilitation work.',
        skills: ['Anatomy', 'Rehabilitation Techniques', 'Patient Care', 'Communication'],
        courses: ['BPT', 'MPT specialisation', 'Clinical practice'],
        progression: ['Junior Physio', 'Physiotherapist', 'Specialist', 'Clinic Owner / Head'],
      },
    ],
  },
  {
    id: 'law',
    name: 'Law',
    careers: [
      {
        name: 'Corporate Lawyer',
        suitableFor: 'Students strong in reading, reasoning, and argumentation.',
        skills: ['Legal Research', 'Drafting', 'Contract Law', 'Negotiation'],
        courses: ['CLAT → 5-year integrated LLB', 'Corporate internships', 'LLM (optional)'],
        progression: ['Junior Associate', 'Associate', 'Senior Associate', 'Partner'],
      },
      {
        name: 'Litigator',
        suitableFor: 'Students who want courtroom practice and independent advocacy.',
        skills: ['Argumentation', 'Evidence Law', 'Court Procedure', 'Public Speaking'],
        courses: ['LLB', 'Chamber practice', 'Bar enrolment'],
        progression: ['Junior Advocate', 'Advocate', 'Senior Counsel track', 'Independent Practice'],
      },
      {
        name: 'Legal Analyst',
        suitableFor: 'Students interested in compliance, research, and corporate legal ops.',
        skills: ['Research', 'Compliance', 'Documentation', 'Attention to Detail'],
        courses: ['LLB / BBA LLB', 'Compliance certifications', 'LPO / KPO roles'],
        progression: ['Legal Analyst', 'Senior Analyst', 'Compliance Manager', 'Head of Legal Ops'],
      },
    ],
  },
  {
    id: 'government',
    name: 'Government & Competitive Exams',
    careers: [
      {
        name: 'Civil Services (UPSC)',
        suitableFor: 'Graduates committed to public service and long-term preparation.',
        skills: ['General Studies', 'Current Affairs', 'Essay Writing', 'Consistency'],
        courses: ['Any UG degree', 'Structured UPSC preparation', 'Test series'],
        progression: ['Aspirant', 'Prelims / Mains', 'Interview', 'IAS / IPS / IFS services'],
      },
      {
        name: 'Banking Exams',
        suitableFor: 'Graduates seeking stable, respected banking careers.',
        skills: ['Quantitative Aptitude', 'Reasoning', 'English', 'Banking Awareness'],
        courses: ['Any UG degree', 'IBPS / SBI exam preparation', 'Mock tests'],
        progression: ['Aspirant', 'Clerk / PO', 'Officer', 'Senior Management'],
      },
      {
        name: 'SSC & State Services',
        suitableFor: 'Graduates targeting central and state government roles.',
        skills: ['Aptitude', 'General Knowledge', 'Language papers', 'Time Management'],
        courses: ['Any UG degree', 'SSC / TNPSC preparation', 'Mock tests'],
        progression: ['Aspirant', 'Group-level posts', 'Officer grades', 'Senior Administration'],
      },
    ],
  },
  {
    id: 'entrepreneurship',
    name: 'Entrepreneurship',
    careers: [
      {
        name: 'Startup Founder',
        suitableFor: 'Self-driven problem-solvers comfortable with risk and ambiguity.',
        skills: ['Problem Validation', 'Sales', 'Finance Basics', 'Leadership'],
        courses: ['Any background', 'Startup incubators', 'Building MVPs'],
        progression: ['Idea stage', 'MVP / Launch', 'Growth stage', 'Scale / Exit'],
      },
      {
        name: 'D2C Brand Builder',
        suitableFor: 'Students interested in brands, e-commerce, and consumer products.',
        skills: ['Branding', 'E-commerce Ops', 'Digital Marketing', 'Unit Economics'],
        courses: ['Any UG degree', 'E-commerce & marketing skills', 'Small launches'],
        progression: ['Side project', 'First brand', 'Multi-channel growth', 'Brand portfolio'],
      },
      {
        name: 'Freelance Consultant',
        suitableFor: 'Skilled individuals who want independent, flexible careers.',
        skills: ['A monetisable skill', 'Client Management', 'Personal Branding', 'Delivery'],
        courses: ['Any background', 'Skill certifications', 'Portfolio of client work'],
        progression: ['First clients', 'Retainers', 'Agency / Studio', 'Premium consulting'],
      },
    ],
  },
  {
    id: 'emerging',
    name: 'Emerging Careers',
    careers: [
      {
        name: 'AI Specialist / Prompt Engineer',
        suitableFor: 'Early adopters who constantly experiment with AI tools.',
        skills: ['Prompt Engineering', 'LLM Workflows', 'Automation', 'Domain Knowledge'],
        courses: ['Any UG degree', 'AI tool certifications', 'Real automation projects'],
        progression: ['AI power user', 'Automation Specialist', 'AI Consultant', 'AI Strategy Lead'],
      },
      {
        name: 'Sustainability Analyst',
        suitableFor: 'Students who care about climate, ESG, and responsible business.',
        skills: ['ESG Frameworks', 'Data Analysis', 'Reporting', 'Policy Awareness'],
        courses: ['Science / Engineering UG', 'ESG certifications', 'Internships'],
        progression: ['Analyst', 'Sustainability Consultant', 'ESG Manager', 'Head of Sustainability'],
      },
      {
        name: 'Creator Economy Professional',
        suitableFor: 'Students who want to build audiences and personal brands.',
        skills: ['Content Creation', 'Audience Building', 'Analytics', 'Monetisation'],
        courses: ['Any background', 'Consistent publishing', 'Brand collaborations'],
        progression: ['Creator', 'Niche authority', 'Brand deals / products', 'Media business'],
      },
    ],
  },
];

export const PLANS = [
  {
    id: 'clarity',
    name: 'Career Clarity Session',
    for: 'For students who need direction.',
    includes: ['One-to-one clarity session', 'Interest & strengths discussion', 'Immediate, practical next steps'],
    featured: false,
  },
  {
    id: 'roadmap',
    name: 'Career Roadmap',
    for: 'For people who want a detailed career action plan.',
    includes: ['Complete career assessment', 'Step-by-step roadmap: education → skills → career', 'Course & college recommendations', 'Skill development plan'],
    featured: true,
  },
  {
    id: 'premium',
    name: 'Premium Career Guidance',
    for: 'For personalised long-term guidance.',
    includes: ['Everything in Career Roadmap', 'Ongoing one-to-one mentoring', 'Placement & job-readiness support', 'Regular progress reviews'],
    featured: false,
  },
];

export const WHY_US = [
  { title: 'Practical Guidance', text: 'Focus on realistic career options and actionable next steps — not vague motivation.' },
  { title: 'Personalised Approach', text: 'Recommendations based on your goals, strengths, and situation — not generic advice.' },
  { title: 'Career + Skills', text: "Don't just choose a degree. Understand the skills required to actually succeed in it." },
  { title: 'Future-Focused', text: 'Understand emerging careers and changing industry requirements before everyone else does.' },
  { title: 'One Career Journey', text: 'Support from school to college to career and beyond — one continuous journey, not one-time advice.' },
];

export const ARTICLES = [
  { title: 'How to Choose the Right Stream After 10th', description: 'A practical framework to match your interests, strengths, and career goals to the right stream.', category: 'Stream Selection' },
  { title: 'AI & Data Science Careers: A Beginner’s Guide', description: 'What data scientists and ML engineers actually do, and how to start preparing early.', category: 'AI & Technology' },
  { title: 'Course Selection After 12th: What Actually Matters', description: 'Cut through the noise — how to evaluate courses by outcomes, not just popularity.', category: 'Course Guides' },
  { title: 'College Selection Checklist: 10 Things to Compare', description: 'Placements, faculty, location, budget — a checklist to compare colleges the right way.', category: 'College Selection' },
  { title: 'The Skills Employers Actually Look For', description: 'Beyond degrees: the practical skills that decide who gets hired and who grows.', category: 'Skills' },
  { title: 'Finance Careers After 12th: CA, CFA, and Beyond', description: 'A clear map of finance and accounting paths, from articleship to investment banking.', category: 'Finance Careers' },
  { title: 'Resume Checklist for Freshers', description: 'The exact sections, structure, and mistakes to avoid on your first resume.', category: 'Resume Tips' },
  { title: 'How to Prepare for Your First Interview', description: 'A simple preparation system for freshers — research, answers, and confidence.', category: 'Interview Preparation' },
  { title: 'Emerging Careers Every Student Should Know', description: 'New-age roles in AI, sustainability, and the creator economy — and how to prepare.', category: 'Career Trends' },
  { title: 'Career Roadmap: From School to Your Dream Job', description: 'The complete School → Stream → Degree → Skills → Placement journey, explained.', category: 'Career Guides' },
];

export const FREE_RESOURCES = [
  'Career Roadmaps',
  'Stream Selection Guide',
  'College Selection Checklist',
  'Skill Development Roadmap',
  'Resume Checklist',
  'Interview Preparation Guide',
];

export const TESTIMONIALS = [
  {
    name: 'Ananya S.',
    role: '12th Grade Student',
    quote: 'I was completely torn between two streams. One session helped me understand my strengths and choose with confidence instead of pressure.',
  },
  {
    name: 'Rahul M.',
    role: 'B.Com Graduate',
    quote: 'The roadmap showed me exactly which certifications and skills to focus on. I stopped guessing and started executing.',
  },
  {
    name: 'Priya K.',
    role: 'Working Professional',
    quote: 'I wanted to switch into a data role but had no idea where to start. The step-by-step plan made the transition feel achievable.',
  },
  {
    name: 'Vignesh R.',
    role: 'Engineering Student',
    quote: 'From internships to placement prep, everything was planned out. I finally knew what to do each semester instead of panicking in the final year.',
  },
];

export const FAQS = [
  { q: 'Who can use CareerWithRaaj?', a: 'Students, graduates, and working professionals at different stages of their career journey — from 8th grade to experienced professionals planning a transition.' },
  { q: 'Can school students get career guidance?', a: 'Yes. Guidance is available from 8th grade onwards, including interest discovery and early career exploration.' },
  { q: 'Do you help with stream selection?', a: 'Yes. We help you choose the right stream after 10th based on your interests, strengths, and future career goals.' },
  { q: 'Can you help me choose a college?', a: 'Yes. College and course selection guidance is available, based on your goals, budget, location, and career outcomes.' },
  { q: 'Do you provide personalised career roadmaps?', a: 'Yes. Every roadmap is built around your current education, interests, target career, and skill level.' },
  { q: 'Do you provide skill development guidance?', a: 'Yes. We identify the skills your target career needs and create a practical learning plan with certifications, projects, and milestones.' },
  { q: 'Can I book a one-to-one consultation?', a: 'Yes. Use the consultation booking option on the website to schedule a one-to-one session.' },
];

export const CONSULTATION_INCLUDES = [
  'One-to-one consultation',
  'Career assessment',
  'Personalised career roadmap',
  'Course recommendations',
  'Skill recommendations',
  'Clear action plan',
];
